import { useMemo, useState } from 'react'
import {
  Container, Stack, Title, Text, Group, Badge, Skeleton,
  UnstyledButton, Box, Tabs, ScrollArea, Paper,
} from '@mantine/core'
import {
  IconInbox, IconInboxOff, IconUserCheck, IconUsers, IconUsersGroup,
  IconMessageQuestion, IconChevronRight, IconGitPullRequest, IconCircleCheck, IconStack2,
} from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { chamadosApi, type InboxItem } from '@/api/chamados'
import { StatusBadge } from '@/components/chamados/StatusBadge'
import { PriorityBadge } from '@/components/chamados/PriorityBadge'
import classes from './ChamadosInbox.module.css'
import { EstadoDeConsulta } from '@/components/ui/EstadoDeConsulta'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { Entrada } from './entrada/Entrada'

type InboxReasonKey = 'aguardando_validacao' | 'assignee' | 'gmud_action' | 'minhas_filas' | 'team_unassigned' | 'team' | 'waiting_response'

const REASON_LABELS: Record<InboxReasonKey, { title: string; icon: typeof IconUsers; emptyMsg: string }> = {
  aguardando_validacao: {
    title: 'Aguardando sua validação',
    icon: IconCircleCheck,
    emptyMsg: 'Nenhum chamado esperando a sua validação.',
  },
  assignee: {
    title: 'Atribuídos a mim',
    icon: IconUserCheck,
    emptyMsg: 'Nenhum chamado atribuído diretamente a você.',
  },
  gmud_action: {
    title: 'GMUD aguardando sua ação',
    icon: IconGitPullRequest,
    emptyMsg: 'Nenhuma mudança esperando ação sua.',
  },
  minhas_filas: {
    title: 'Minhas filas',
    icon: IconStack2,
    emptyMsg: 'Nenhum chamado sem responsável nas suas filas.',
  },
  team_unassigned: {
    title: 'Do meu time, sem responsável',
    icon: IconUsersGroup,
    emptyMsg: 'Nenhum chamado sem responsável no seu time.',
  },
  team: {
    title: 'Para o meu time',
    icon: IconUsers,
    emptyMsg: 'Nenhum chamado pendente para o seu time.',
  },
  waiting_response: {
    title: 'Aguardando minha resposta',
    icon: IconMessageQuestion,
    emptyMsg: 'Você não tem chamados aguardando sua resposta.',
  },
}

// team_unassigned acima de team: trabalho órfão é acionável, a seção do
// time é informativa (spec sub-tarefas 21/07). gmud_action logo abaixo de
// assignee: mudança em produção pesa mais que chamado do time e menos que
// atribuição nominal. aguardando_validacao na frente de tudo: é o que só você
// pode fazer, e o prazo corre (Onda 3, spec 2026-09-27 §6.2). Mesma prioridade
// que o backend aplica no inbox_reason.
// minhas_filas (TASK-12181) logo depois do que é nominal: é o trabalho órfão da fila da
// Organização atendente, que pode ser de um tenant em que o usuário não está.
const SECTION_ORDER: InboxReasonKey[] = ['aguardando_validacao', 'assignee', 'gmud_action', 'minhas_filas', 'team_unassigned', 'team', 'waiting_response']

// Idade relativa em PT-BR: "agora", "há 12 min", "há 3 h", "ontem", "há 4 d", data.
function relativeTime(iso: string): string {
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ''
  const diffMs = Date.now() - then
  const min = Math.floor(diffMs / 60_000)
  if (min < 1) return 'agora'
  if (min < 60) return `há ${min} min`
  const h = Math.floor(min / 60)
  if (h < 24) return `há ${h} h`
  const d = Math.floor(h / 24)
  if (d === 1) return 'ontem'
  if (d < 7) return `há ${d} d`
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
}

/** Linha densa de chamado (formato tabela). Colunas colapsam em telas estreitas
 *  via CSS; a coluna de título sempre ocupa o espaço restante. */
function InboxRow({
  item,
  onOpen,
  indented = false,
}: {
  item: InboxItem
  onOpen: () => void
  indented?: boolean
}) {
  const requester = item.requester_name || item.requester_email
  const age = relativeTime(item.created_at)

  return (
    <UnstyledButton
      className={`${classes.row}${indented ? ` ${classes.rowIndented}` : ''}`}
      onClick={onOpen}
      aria-label={`Chamado #${item.id}: ${item.title}`}
    >
      <span className={classes.cellId}>#{item.id}</span>
      <span className={classes.cellTitle}>{item.title}</span>
      <span className={classes.cellTenant}>
        <span className={classes.tenantTag}>{item.tenant_name ?? `#${item.tenant_id}`}</span>
      </span>
      <span className={classes.cellStatus}>
        <StatusBadge status={item.status} size="xs" />
      </span>
      <span className={classes.cellPriority}>
        <PriorityBadge priority={item.priority} size="xs" />
      </span>
      <span className={classes.cellRequester}>{requester || '—'}</span>
      <span className={classes.cellAge}>{age}</span>
    </UnstyledButton>
  )
}

/**
 * InboxGroup — agrupa as sub-tarefas de um mesmo chamado-pai sob um cabeçalho
 * colapsável (colapsado por padrão). Reduz o ruído quando um pai tem várias
 * filhas na mesma aba. O contador fica sempre visível (não esconde trabalho).
 */
function InboxGroup({
  parentId,
  subtasks,
  isOpen,
  onToggle,
  onOpenTicket,
}: {
  parentId: number
  subtasks: InboxItem[]
  isOpen: boolean
  onToggle: () => void
  onOpenTicket: (id: number) => void
}) {
  const first = subtasks[0]
  const parentLabel = first.parent_title ?? `Chamado #${parentId}`

  return (
    <div className={classes.group}>
      <UnstyledButton
        className={classes.groupHeader}
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-label={isOpen ? `Colapsar sub-tarefas de ${parentLabel}` : `Expandir sub-tarefas de ${parentLabel}`}
      >
        <span className={classes.groupChev} data-open={isOpen}>
          <IconChevronRight size={15} />
        </span>
        <IconUsersGroup size={15} style={{ color: 'var(--ai-text-secondary)', flexShrink: 0 }} />
        <span className={classes.ticketId}>#{parentId}</span>
        <span className={classes.groupTitle}>{parentLabel}</span>
        <Badge size="sm" radius="sm" variant="light" color="gray">
          {subtasks.length} sub-tarefas
        </Badge>
      </UnstyledButton>
      {isOpen && (
        <div className={classes.groupChildren}>
          <Stack gap={4}>
            {subtasks.map(c => (
              <InboxRow key={c.id} item={c} indented onOpen={() => onOpenTicket(c.id)} />
            ))}
          </Stack>
        </div>
      )}
    </div>
  )
}

/** Sequência de render de uma aba: agrupa filhas do mesmo pai (>= 2) sob um
 * grupo colapsável; itens avulsos / pais com 1 filha na aba ficam planos. */
function buildSectionEntries(sectionItems: InboxItem[]) {
  const countByParent = new Map<number, number>()
  for (const it of sectionItems) {
    if (it.parent_task_id != null) {
      countByParent.set(it.parent_task_id, (countByParent.get(it.parent_task_id) ?? 0) + 1)
    }
  }
  const seen = new Set<number>()
  const entries: Array<
    | { kind: 'item'; item: InboxItem }
    | { kind: 'group'; parentId: number; children: InboxItem[] }
  > = []
  for (const it of sectionItems) {
    const pid = it.parent_task_id
    if (pid != null && (countByParent.get(pid) ?? 0) >= 2) {
      if (seen.has(pid)) continue
      seen.add(pid)
      entries.push({
        kind: 'group',
        parentId: pid,
        children: sectionItems.filter(x => x.parent_task_id === pid),
      })
    } else {
      entries.push({ kind: 'item', item: it })
    }
  }
  return entries
}

/** Cabeçalho de colunas da lista (formato tabela densa). */
function ListHeader() {
  return (
    <div className={`${classes.row} ${classes.listHead}`} aria-hidden>
      <span className={classes.cellId}>#</span>
      <span className={classes.cellTitle}>Chamado</span>
      <span className={classes.cellTenant}>Organização</span>
      <span className={classes.cellStatus}>Status</span>
      <span className={classes.cellPriority}>Prioridade</span>
      <span className={classes.cellRequester}>Solicitante</span>
      <span className={classes.cellAge}>Aberto</span>
    </div>
  )
}

function SectionList({
  reason,
  sectionItems,
  openGroups,
  toggleGroup,
  navigate,
}: {
  reason: InboxReasonKey
  sectionItems: InboxItem[]
  openGroups: Set<number>
  toggleGroup: (id: number) => void
  navigate: (path: string) => void
}) {
  const cfg = REASON_LABELS[reason]

  if (sectionItems.length === 0) {
    const Icon = cfg.icon
    return (
      <Box className={classes.emptyState}>
        <Icon size={40} className={classes.emptyIcon} />
        <Text size="sm" c="dimmed">{cfg.emptyMsg}</Text>
      </Box>
    )
  }

  const entries = buildSectionEntries(sectionItems)

  return (
    <ScrollArea.Autosize mah="calc(100vh - 260px)" type="auto" offsetScrollbars>
      <div className={classes.list}>
        <ListHeader />
        <Stack gap={4}>
          {entries.map(entry =>
            entry.kind === 'group' ? (
              <InboxGroup
                key={`g-${entry.parentId}`}
                parentId={entry.parentId}
                subtasks={entry.children}
                isOpen={openGroups.has(entry.parentId)}
                onToggle={() => toggleGroup(entry.parentId)}
                onOpenTicket={(id) => navigate(`/chamados/${id}`)}
              />
            ) : (
              <InboxRow
                key={entry.item.id}
                item={entry.item}
                onOpen={() => navigate(`/chamados/${entry.item.id}`)}
              />
            )
          )}
        </Stack>
      </div>
    </ScrollArea.Autosize>
  )
}

/**
 * Entrada de chamados. Com `mailbox_ui_enabled` (decidido por usuário no backend: a flag global
 * ou a lista `MAILBOX_UI_USER_IDS`, spec 2026-10-19 §8), a Entrada no modelo Mailbox; sem ela,
 * a tela de abas de sempre, sem mudar nada.
 */
export function ChamadosInbox() {
  const { mailbox_ui_enabled } = useFeatureFlags()
  return mailbox_ui_enabled ? <Entrada /> : <ChamadosInboxEmAbas />
}

function ChamadosInboxEmAbas() {
  const navigate = useNavigate()
  const [openGroups, setOpenGroups] = useState<Set<number>>(new Set())
  const [activeTab, setActiveTab] = useState<string | null>(null)
  const toggleGroup = (id: number) =>
    setOpenGroups(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  const consulta = useQuery({
    queryKey: ['chamados-inbox'],
    queryFn: async () => {
      const res = await chamadosApi.getInbox()
      return res.data
    },
    staleTime: 60_000,
  })
  // Fonte separada: a aba vem de outra consulta (`aba=minhas_filas`), sem o filtro de
  // user_tenants, porque a fila atende tenants em que o membro não está.
  const filas = useQuery({
    queryKey: ['chamados-inbox', 'minhas_filas'],
    queryFn: async () => (await chamadosApi.getInbox({ aba: 'minhas_filas' })).data,
    staleTime: 60_000,
  })
  const itemsDasFilas = useMemo(() => filas.data?.items ?? [], [filas.data])

  const { data, isLoading } = consulta
  const items = useMemo(() => data?.items ?? [], [data])
  const groups = data?.groups ?? {}
  const hasTeamSection = 'team' in groups

  const itemsByReason: Record<InboxReasonKey, InboxItem[]> = useMemo(() => ({
    aguardando_validacao: items.filter(it => it.inbox_reason === 'aguardando_validacao'),
    assignee: items.filter(it => it.inbox_reason === 'assignee'),
    gmud_action: items.filter(it => it.inbox_reason === 'gmud_action'),
    minhas_filas: itemsDasFilas,
    team_unassigned: items.filter(it => it.inbox_reason === 'team_unassigned'),
    team: items.filter(it => it.inbox_reason === 'team'),
    waiting_response: items.filter(it => it.inbox_reason === 'waiting_response'),
  }), [items, itemsDasFilas])

  // Abas visíveis: oculta as seções de time quando o backend não trouxe grupo `team`.
  const visibleTabs = useMemo(
    () => SECTION_ORDER.filter(r => {
      if (r === 'team' || r === 'team_unassigned') return hasTeamSection
      // Só quem é membro de alguma fila vê chamado nela; para os outros a aba seria ruído.
      if (r === 'minhas_filas') return itemsDasFilas.length > 0
      return true
    }),
    [hasTeamSection, itemsDasFilas],
  )

  // Aba inicial: primeira aba com itens; se todas vazias, a primeira visível.
  const defaultTab = useMemo(() => {
    const withItems = visibleTabs.find(r => itemsByReason[r].length > 0)
    return withItems ?? visibleTabs[0] ?? 'assignee'
  }, [visibleTabs, itemsByReason])

  const totalItems = items.length + itemsDasFilas.length

  if (isLoading) {
    return (
      <Container size="xl" py="md">
        <Stack>
          <Skeleton height={44} width={260} radius="md" />
          <Skeleton height={38} radius="md" />
          <Skeleton height={320} radius="md" />
        </Stack>
      </Container>
    )
  }

  // Sem isto, falha de rede virava `items = []` e a tela dizia "Nenhum chamado atribuido
  // diretamente a voce" — afirmando sobre a fila de alguem algo que nao tinha como saber. Numa
  // caixa de entrada de trabalho diario, isso e o pior desfecho: ninguem investiga o que parece
  // vazio.
  if (consulta.isError && data === undefined) {
    return (
      <Container size="xl" py="md">
        <EstadoDeConsulta consulta={consulta}>{null}</EstadoDeConsulta>
      </Container>
    )
  }

  if (totalItems === 0) {
    return (
      <Container size="md" py="xl">
        <Stack align="center" gap="md">
          <IconInboxOff size={64} style={{ opacity: 0.4 }} />
          <Title order={3}>Caixa de entrada vazia</Title>
          <Text c="dimmed">Nada precisa da sua atenção agora.</Text>
          <UnstyledButton onClick={() => navigate('/chamados?scope=org')}>
            <Text size="sm" c="inovareTeal">Ver todos os chamados →</Text>
          </UnstyledButton>
        </Stack>
      </Container>
    )
  }

  return (
    <Container size="xl" py="md" className={classes.page}>
      <Group mb="lg" gap="sm" align="center">
        <span className={classes.headerIcon}>
          <IconInbox size={22} />
        </span>
        <div style={{ flex: 1 }}>
          {/* O titulo vem do layout desde 2026-08-13 (routeHeader + PageHeader). Estava aqui
          tambem, repetindo o nome da rota. */}
          <Text size="sm" c="dimmed">O que precisa da sua atenção</Text>
        </div>
        <UnstyledButton onClick={() => navigate('/chamados?scope=org')} className={classes.headerLink}>
          <Text size="xs" c="dimmed">Ver todos os chamados →</Text>
        </UnstyledButton>
      </Group>

      <Paper className={classes.board} radius="md">
        <Tabs
          value={activeTab ?? defaultTab}
          onChange={setActiveTab}
          variant="default"
          keepMounted={false}
        >
          <Tabs.List className={classes.tabsList}>
            {visibleTabs.map(reason => {
              const cfg = REASON_LABELS[reason]
              const count = itemsByReason[reason].length
              const Icon = cfg.icon
              const isPrimary = reason === 'assignee' || reason === 'aguardando_validacao'
              return (
                <Tabs.Tab
                  key={reason}
                  value={reason}
                  leftSection={<Icon size={16} />}
                  rightSection={
                    <Badge
                      size="sm"
                      radius="sm"
                      variant={count > 0 && isPrimary ? 'filled' : 'light'}
                      color={count > 0 && isPrimary ? 'inovareTeal' : 'gray'}
                    >
                      {count}
                    </Badge>
                  }
                >
                  {cfg.title}
                </Tabs.Tab>
              )
            })}
          </Tabs.List>

          {visibleTabs.map(reason => (
            <Tabs.Panel key={reason} value={reason} pt="sm">
              <SectionList
                reason={reason}
                sectionItems={itemsByReason[reason]}
                openGroups={openGroups}
                toggleGroup={toggleGroup}
                navigate={navigate}
              />
            </Tabs.Panel>
          ))}
        </Tabs>
      </Paper>
    </Container>
  )
}
